import {
  BarChart3,
  Boxes,
  Building2,
  Calculator,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Database,
  Gauge,
  LayoutDashboard,
  Menu,
  Megaphone,
  PackageSearch,
  Radio,
  Search,
  Settings2,
  ShieldCheck,
  Store,
  Tags,
  TrendingUp,
  Undo2,
  UserRound,
  Warehouse,
  KeyRound,
  LogOut,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { routePermission, useAuth } from '../auth'
import { preloadCalculatorBootstrap } from '../calculatorBootstrapCache'
import { useProductData } from '../data'
import { DOUYIN_SHUIJU_STORE_ID, DOUYIN_YOUHE_STORE_ID, DOUYIN_STORE_IDS, douyinPath } from '../douyinStore'
import { usePlatformRegistry, type PlatformRegistryStore } from '../platform'
import { DataFreshnessBadge } from './DataFreshnessBadge'

interface SidebarItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
  status?: string
  extraPaths?: string[]
}

interface SidebarSection {
  label: string
  items: SidebarItem[]
}

type WorkspaceModule = 'overview' | 'snapshot' | 'profit' | 'analysis' | 'afterSales' | 'products' | 'spu' | 'sku' | 'productMaster' | 'marketing' | 'costs' | 'data'

interface WorkspaceOption {
  id: string
  kind: 'company' | 'store'
  route: string | null
  platformId: string
  platformName: string
  storeName: string
  status: string
}

const JD_STORE_ID = 'jd-588485'
const PDD_STORE_ID = 'pdd-beijuxiong-youhe'
const DY_STORE_ID = DOUYIN_SHUIJU_STORE_ID
const DY_YOUHE_STORE_ID = DOUYIN_YOUHE_STORE_ID

const companySections: SidebarSection[] = [
  {
    label: '公司经营',
    items: [{ to: '/company', label: '公司总览', icon: LayoutDashboard, end: true }],
  },
  {
    label: '公司资产',
    items: [
      { to: '/company/products', label: '公司产品库', icon: Boxes, end: true },
      { to: '/company/mappings', label: '平台商品映射', icon: Tags, end: true },
    ],
  },
]

const jdSections: SidebarSection[] = [
  {
    label: '经营',
    items: [
      { to: '/', label: '店铺总览', icon: LayoutDashboard, end: true },
      { to: '/realtime', label: '经营快照', icon: Gauge, status: '暂停' },
      { to: '/profit', label: '利润中心', icon: CircleDollarSign },
      { to: '/analysis', label: '机会与风险', icon: TrendingUp, extraPaths: ['/analysis/'] },
      { to: '/jd/orders', label: '订单口径', icon: Undo2, end: true },
    ],
  },
  {
    label: '商品运营',
    items: [
      { to: '/spu', label: 'SPU商品', icon: Boxes, extraPaths: ['/product/spu/'] },
      { to: '/sku', label: 'SKU商品', icon: PackageSearch, extraPaths: ['/product/sku/'] },
      { to: '/product-master', label: '京东SKU资料', icon: Tags },
      { to: '/new-products', label: '新品分析', icon: BarChart3 },
      { to: '/jd/warehouse-slow-moving', label: '京东仓滞销', icon: Warehouse, end: true },
      { to: '/inventory', label: '库存分析', icon: Database },
    ],
  },
  {
    label: '推广营销',
    items: [
      { to: '/advertising/orders', label: '广告效果', icon: BarChart3, end: true },
      { to: '/advertising/fee-ratio', label: '费比达标', icon: Gauge, end: true },
      { to: '/advertising', label: '广告费用', icon: Megaphone, end: true },
      { to: '/campaign-analysis', label: '活动分析', icon: BarChart3 },
    ],
  },
  {
    label: '财务与数据',
    items: [
      { to: '/jd/profit-calculator', label: '利润计算器', icon: Calculator },
      { to: '/jd/costs', label: '店铺成本', icon: Settings2 },
      { to: '/jd/data-update', label: '数据更新', icon: Database },
    ],
  },
]

const pddSections: SidebarSection[] = [
  {
    label: '经营',
    items: [{ to: '/pinduoduo', label: '店铺总览', icon: LayoutDashboard, end: true }],
  },
  {
    label: '商品运营',
    items: [
      { to: '/pinduoduo/spu', label: 'SPU商品', icon: Boxes },
      { to: '/pinduoduo/sku', label: 'SKU商品', icon: PackageSearch },
      { to: '/pinduoduo/product-master', label: '拼多多SKU资料', icon: Tags },
      { to: '/pinduoduo/products', label: '推广与活动商品', icon: BarChart3, extraPaths: ['/pinduoduo/product/'] },
    ],
  },
  {
    label: '财务与数据',
    items: [
      { to: '/pinduoduo/after-sales', label: '退货与售后', icon: Undo2, end: true },
      { to: '/pinduoduo/profit-calculator', label: '利润计算器', icon: Calculator, end: true },
      { to: '/pinduoduo/costs', label: '店铺成本', icon: Settings2, end: true },
      { to: '/pinduoduo/data-upload', label: '数据更新', icon: Database, end: true },
    ],
  },
]

const dySections = (storeId: string): SidebarSection[] => [
  {
    label: '商品运营',
    items: [
      { to: douyinPath(storeId, '/spu'), label: 'SPU商品', icon: Boxes, extraPaths: [douyinPath(storeId, '/spu/')] },
      { to: douyinPath(storeId, '/sku'), label: 'SKU商品', icon: PackageSearch },
    ],
  },
  {
    label: '直播',
    items: [{ to: douyinPath(storeId, '/live'), label: '直播场次', icon: Radio, extraPaths: [douyinPath(storeId, '/live/')] }],
  },
  {
    label: '财务与数据',
    items: [{ to: douyinPath(storeId, '/costs'), label: '店铺成本', icon: Settings2, end: true }],
  },
]

const douyinWorkspaceDefinition = (storeId: string) => ({
  route: douyinPath(storeId, '/spu'),
  sections: dySections(storeId),
  moduleRoutes: {
    overview: douyinPath(storeId, '/spu'), products: douyinPath(storeId, '/spu'), spu: douyinPath(storeId, '/spu'),
    sku: douyinPath(storeId, '/sku'), marketing: douyinPath(storeId, '/live'), costs: douyinPath(storeId, '/costs'),
  },
})

const workspaceDefinitions: Record<string, {
  route: string
  sections: SidebarSection[]
  moduleRoutes: Partial<Record<WorkspaceModule, string>>
}> = {
  company: {
    route: '/company',
    sections: companySections,
    moduleRoutes: { overview: '/company', products: '/company/products', data: '/company' },
  },
  [JD_STORE_ID]: {
    route: '/',
    sections: jdSections,
    moduleRoutes: {
      overview: '/', snapshot: '/realtime', profit: '/profit', analysis: '/analysis', afterSales: '/analysis/refunds',
      products: '/sku', spu: '/spu', sku: '/sku', productMaster: '/product-master', marketing: '/advertising/orders', costs: '/jd/costs', data: '/jd/data-update',
    },
  },
  [PDD_STORE_ID]: {
    route: '/pinduoduo',
    sections: pddSections,
    moduleRoutes: {
      overview: '/pinduoduo', products: '/pinduoduo/products', spu: '/pinduoduo/spu',
      sku: '/pinduoduo/sku', productMaster: '/pinduoduo/product-master', afterSales: '/pinduoduo/after-sales', costs: '/pinduoduo/costs',
      data: '/pinduoduo/data-upload',
    },
  },
  [DY_STORE_ID]: douyinWorkspaceDefinition(DY_STORE_ID),
  [DY_YOUHE_STORE_ID]: douyinWorkspaceDefinition(DY_YOUHE_STORE_ID),
}

const pageTitles: Array<[string, string]> = [
  ['/admin/accounts', '账号与权限'],
  ['/company/products', '公司产品库'], ['/company/mappings', '平台商品映射'], ['/company', '公司总览'],
  ['/pinduoduo/product-master', '拼多多SKU资料'], ['/pinduoduo/products', '推广与活动商品'],
  ['/pinduoduo/after-sales', '退货与售后分析'], ['/pinduoduo/data-upload', '数据更新'], ['/pinduoduo/profit-calculator', '利润计算器'],
  ['/jd/profit-calculator', '利润计算器'],
  ['/jd/data-update', '数据更新'],
  ['/jd/warehouse-slow-moving', '京东仓滞销分析'],
  ['/pinduoduo/product/', '商品推广详情'], ['/pinduoduo/spu/', 'SPU经营详情'], ['/pinduoduo/spu', 'SPU商品'], ['/pinduoduo/sku', 'SKU商品'],
  ['/pinduoduo/costs', '店铺成本'], ['/pinduoduo', '店铺总览'],
  ['/douyin/spu/', 'SPU经营详情'], ['/douyin/spu', 'SPU商品'], ['/douyin/sku', 'SKU商品'],
  ['/douyin/live/', '直播场次详情'], ['/douyin/live', '直播场次'], ['/douyin/costs', '店铺成本'],
  ['/product-master', '京东SKU资料'], ['/product/', '商品详情'], ['/campaign-analysis', '活动分析'],
  ['/new-products', '新品分析'], ['/advertising/orders', '广告效果明细'], ['/advertising/fee-ratio', '京东费比达标'], ['/advertising/spend-comparison', '京东费比达标'], ['/advertising', '广告费用'], ['/inventory', '库存分析'],
  ['/realtime', '经营快照'], ['/profit', '利润中心'], ['/analysis', '机会与风险'],
  ['/jd/costs', '店铺成本'], ['/spu', 'SPU商品'], ['/sku', 'SKU商品'], ['/data', '数据状态'],
]

function storeId(store: PlatformRegistryStore) {
  return store.storeId || store.id || ''
}

function storeName(store: PlatformRegistryStore) {
  return store.displayName || store.name || storeId(store) || '未命名店铺'
}

function moduleForPath(pathname: string): WorkspaceModule {
  if (pathname.startsWith('/realtime')) return 'snapshot'
  if (pathname.startsWith('/profit')) return 'profit'
  if (pathname.startsWith('/analysis/refunds') || pathname.startsWith('/pinduoduo/after-sales')) return 'afterSales'
  if (pathname.startsWith('/analysis')) return 'analysis'
  if (pathname.startsWith('/advertising') || pathname.startsWith('/campaign-analysis')) return 'marketing'
  if (pathname.startsWith('/jd/costs') || pathname.startsWith('/pinduoduo/costs') || /^\/douyin\/(?:dy-[^/]+\/)?costs(?:\/|$)/.test(pathname)) return 'costs'
  if (/^\/douyin\/(?:dy-[^/]+\/)?live(?:\/|$)/.test(pathname)) return 'marketing'
  if (pathname.startsWith('/data') || pathname.startsWith('/jd/data-update') || pathname.startsWith('/pinduoduo/data-upload')) return 'data'
  if (pathname.startsWith('/pinduoduo/product-master') || pathname.startsWith('/product-master')) return 'productMaster'
  if (pathname.startsWith('/pinduoduo/spu') || /^\/douyin\/(?:dy-[^/]+\/)?spu(?:\/|$)/.test(pathname) || pathname.startsWith('/spu')) return 'spu'
  if (pathname.startsWith('/pinduoduo/sku') || /^\/douyin\/(?:dy-[^/]+\/)?sku(?:\/|$)/.test(pathname) || pathname.startsWith('/sku')) return 'sku'
  if (
    pathname.startsWith('/product/') || pathname.startsWith('/new-products')
    || pathname.startsWith('/inventory') || pathname.startsWith('/jd/warehouse-slow-moving') || pathname.startsWith('/company/products')
    || pathname.startsWith('/company/mappings') || pathname.startsWith('/pinduoduo/products')
    || pathname.startsWith('/pinduoduo/product/')
  ) return 'products'
  return 'overview'
}

function pageTitleFor(pathname: string) {
  if (pathname === '/') return '店铺总览'
  const douyinPage = pathname.match(/^\/douyin\/(?:dy-[^/]+\/)?(spu|sku|live|costs)(?:\/([^/]+))?/)
  if (douyinPage) {
    if (douyinPage[1] === 'spu') return douyinPage[2] ? 'SPU经营详情' : 'SPU商品'
    if (douyinPage[1] === 'sku') return 'SKU商品'
    if (douyinPage[1] === 'live') return douyinPage[2] ? '直播场次详情' : '直播场次'
    return '店铺成本'
  }
  return pageTitles.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix))?.[1] || '经营台账'
}

function SidebarNavigation({
  sections,
  collapsed,
  onNavigate,
}: {
  sections: SidebarSection[]
  collapsed: boolean
  onNavigate: () => void
}) {
  const location = useLocation()
  return (
    <nav className="sidebar-navigation" aria-label="当前经营主体业务导航">
      {sections.map((section) => (
        <section className="sidebar-nav-section" key={section.label}>
          <h2>{section.label}</h2>
          {section.items.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                onClick={onNavigate}
                className={({ isActive }) => isActive || item.extraPaths?.some((path) => location.pathname.startsWith(path))
                  ? 'sidebar-nav-link is-active'
                  : 'sidebar-nav-link'}
              >
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
                {item.status ? <small>{item.status}</small> : null}
              </NavLink>
            )
          })}
        </section>
      ))}
    </nav>
  )
}

export function AppShell() {
  const auth = useAuth()
  const { data, jdAdvertisingOrders, selectedDate, setSelectedDate } = useProductData()
  const platformRegistry = usePlatformRegistry()
  const navigate = useNavigate()
  const location = useLocation()
  const workspaceRef = useRef<HTMLDivElement>(null)
  const accountRef = useRef<HTMLDivElement>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [workspaceOpen, setWorkspaceOpen] = useState(false)
  const [workspaceSearch, setWorkspaceSearch] = useState('')
  const [switchNotice, setSwitchNotice] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const deferredSearchQuery = useDeferredValue(searchQuery)

  const isRealtime = location.pathname.startsWith('/realtime')
    || (
      (location.pathname === '/spu' || location.pathname === '/sku' || location.pathname.startsWith('/product/spu/') || location.pathname.startsWith('/product/sku/'))
      && new URLSearchParams(location.search).get('period') === 'realtime'
    )
  const isPinduoduo = location.pathname.startsWith('/pinduoduo') || location.pathname.startsWith('/shared-calculations/pinduoduo/')
  const isDouyin = location.pathname.startsWith('/douyin')
  const douyinStoreMatch = location.pathname.match(/^\/douyin\/(dy-[^/]+)(?:\/|$)/)
  const isCompany = location.pathname.startsWith('/company')
  const isCalculator = location.pathname === '/jd/profit-calculator' || location.pathname === '/pinduoduo/profit-calculator' || location.pathname.startsWith('/shared-calculations/')
  const isFeeRatio = location.pathname.startsWith('/advertising/fee-ratio') || location.pathname.startsWith('/advertising/spend-comparison')
  const isAdvertisingOrders = location.pathname.startsWith('/advertising/orders')
  let currentWorkspaceId = JD_STORE_ID
  if (isCompany) currentWorkspaceId = 'company'
  else if (isPinduoduo) currentWorkspaceId = PDD_STORE_ID
  else if (isDouyin) currentWorkspaceId = douyinStoreMatch && DOUYIN_STORE_IDS.has(douyinStoreMatch[1]) ? douyinStoreMatch[1] : DY_STORE_ID
  const currentWorkspaceDefinition = workspaceDefinitions[currentWorkspaceId]
  const sections = useMemo(() => currentWorkspaceDefinition.sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const permission = routePermission(item.to)
        return !permission || auth.canAccess(permission[0], permission[1])
      }),
    }))
    .filter((section) => section.items.length > 0), [auth, currentWorkspaceDefinition.sections])
  const firstAuthorizedRoute = (workspaceId: string) => {
    const definition = workspaceDefinitions[workspaceId]
    if (!definition) return null
    for (const section of definition.sections) {
      for (const item of section.items) {
        const permission = routePermission(item.to)
        if (!permission || auth.canAccess(permission[0], permission[1])) return item.to
      }
    }
    return null
  }
  const currentWorkspaceHome = firstAuthorizedRoute(currentWorkspaceId) || '/forbidden'

  useEffect(() => {
    const userId = auth.user?.id
    if (!userId) return
    const target = currentWorkspaceId === JD_STORE_ID && auth.canAccess(JD_STORE_ID, 'jd.profit_calculator')
      ? ['jd', '/api/calculators/jd/bootstrap'] as const
      : currentWorkspaceId === PDD_STORE_ID && auth.canAccess(PDD_STORE_ID, 'pdd.profit_calculator')
        ? ['pdd', '/api/calculators/pinduoduo/bootstrap'] as const
        : null
    if (!target) return
    const timer = window.setTimeout(() => preloadCalculatorBootstrap(userId, target[0], target[1]), 600)
    return () => window.clearTimeout(timer)
  }, [auth, currentWorkspaceId])

  const workspaceOptions = useMemo<WorkspaceOption[]>(() => {
    const registry = platformRegistry.data
    const platformNames = new Map((registry?.platforms || []).map((platform) => {
      const id = platform.platformId || platform.id || platform.slug || ''
      return [id, platform.displayName || platform.name || platform.label || id]
    }))
    const options: WorkspaceOption[] = (registry?.stores || []).map((store) => {
      const id = storeId(store)
      const platformId = store.platformId || store.platform || ''
      return {
        id,
        kind: 'store',
        route: workspaceDefinitions[id]?.route || null,
        platformId,
        platformName: platformNames.get(platformId) || platformId,
        storeName: storeName(store),
        status: store.status || 'unknown',
      }
    })
    const knownStores: WorkspaceOption[] = [
      { id: JD_STORE_ID, kind: 'store', route: '/', platformId: 'jd', platformName: '京东', storeName: '杯具熊旗舰店', status: 'active' },
      { id: PDD_STORE_ID, kind: 'store', route: '/pinduoduo', platformId: 'pinduoduo', platformName: '拼多多', storeName: '杯具熊优合专卖店', status: 'active' },
      { id: DY_STORE_ID, kind: 'store', route: douyinPath(DY_STORE_ID, '/spu'), platformId: 'douyin', platformName: '抖音', storeName: 'BEDDY.POP水具旗舰店', status: 'active' },
      { id: DY_YOUHE_STORE_ID, kind: 'store', route: douyinPath(DY_YOUHE_STORE_ID, '/spu'), platformId: 'douyin', platformName: '抖音', storeName: '杯具熊优合专卖店', status: 'active' },
    ]
    for (const known of knownStores) if (!options.some((option) => option.id === known.id)) options.push(known)
    return options.filter((option) => auth.user?.isAdmin || auth.user?.permissions.some((permission) => permission.storeId === option.id))
  }, [auth.user, platformRegistry.data])

  const currentWorkspace = useMemo<WorkspaceOption>(() => {
    if (currentWorkspaceId === 'company') return {
      id: 'company', kind: 'company', route: '/company', platformId: 'company', platformName: '全公司',
      storeName: '全公司经营中心', status: 'active',
    }
    return workspaceOptions.find((option) => option.id === currentWorkspaceId) || {
      id: currentWorkspaceId, kind: 'store', route: currentWorkspaceDefinition.route,
      platformId: isPinduoduo ? 'pinduoduo' : isDouyin ? 'douyin' : 'jd',
      platformName: isPinduoduo ? '拼多多' : isDouyin ? '抖音' : '京东',
      storeName: isPinduoduo ? '杯具熊优合专卖店' : isDouyin ? currentWorkspaceId === DY_YOUHE_STORE_ID ? '杯具熊优合专卖店' : 'BEDDY.POP水具旗舰店' : '杯具熊旗舰店', status: 'active',
    }
  }, [currentWorkspaceDefinition.route, currentWorkspaceId, isDouyin, isPinduoduo, workspaceOptions])

  const groupedWorkspaces = useMemo(() => {
    const needle = workspaceSearch.trim().toLowerCase()
    const groups = new Map<string, { name: string; stores: WorkspaceOption[] }>()
    for (const option of workspaceOptions) {
      if (needle && !`${option.platformName} ${option.storeName} ${option.id}`.toLowerCase().includes(needle)) continue
      const group = groups.get(option.platformId) || { name: option.platformName, stores: [] }
      group.stores.push(option)
      groups.set(option.platformId, group)
    }
    return [...groups.entries()]
  }, [workspaceOptions, workspaceSearch])

  const searchResults = useMemo(() => {
    const query = deferredSearchQuery.trim().toLowerCase()
    if (!data || query.length < 2) return []
    return data.products
      .map((product) => {
        const exactCodeMatch = [product.id, product.jdSkuId, product.merchantSku]
          .some((value) => value && value.toLowerCase() === query)
        const searchable = [
          product.name, product.nameNote || '', product.id, product.jdSkuId,
          product.merchantSku, product.itemNumber,
        ].join(' ').toLowerCase()
        return { product, exactCodeMatch, matches: searchable.includes(query) }
      })
      .filter((item) => item.matches)
      .sort((a, b) => Number(b.exactCodeMatch) - Number(a.exactCodeMatch))
      .slice(0, 8)
      .map((item) => item.product)
  }, [data, deferredSearchQuery])

  useEffect(() => {
    setMobileSidebarOpen(false)
    setWorkspaceOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (workspaceRef.current && !workspaceRef.current.contains(event.target as Node)) setWorkspaceOpen(false)
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setAccountOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setWorkspaceOpen(false)
        setAccountOpen(false)
        setMobileSidebarOpen(false)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  useEffect(() => {
    if (!switchNotice) return
    const timer = window.setTimeout(() => setSwitchNotice(''), 3200)
    return () => window.clearTimeout(timer)
  }, [switchNotice])

  const switchWorkspace = (target: WorkspaceOption) => {
    if (!target.route) return
    const currentModule = moduleForPath(location.pathname)
    const targetModuleRoutes = workspaceDefinitions[target.id]?.moduleRoutes
    const candidateRoute = targetModuleRoutes?.[currentModule]
    const candidatePermission = candidateRoute ? routePermission(candidateRoute) : null
    const canUseCandidate = candidateRoute && (!candidatePermission || auth.canAccess(candidatePermission[0], candidatePermission[1]))
    const targetRoute = canUseCandidate ? candidateRoute : firstAuthorizedRoute(target.id)
    if (!targetRoute) return
    if (!canUseCandidate && currentModule !== 'overview') {
      setSwitchNotice(`已切换到${target.storeName}；当前模块尚未接入，已返回总览。`)
    }
    setWorkspaceOpen(false)
    setWorkspaceSearch('')
    navigate(targetRoute)
  }

  const switchToCompany = () => switchWorkspace({
    id: 'company', kind: 'company', route: '/company', platformId: 'company', platformName: '全公司',
    storeName: '全公司经营中心', status: 'active',
  })

  const openProduct = (level: 'spu' | 'sku', id: string) => {
    setSearchQuery('')
    setSearchOpen(false)
    navigate(`/product/${level}/${id}`)
  }

  const WorkspaceIcon = currentWorkspace.kind === 'company' ? Building2 : Store
  const pageTitle = pageTitleFor(location.pathname)
  const showCompanyWorkspace = Boolean(auth.user?.isAdmin || auth.user?.permissions.some((permission) => permission.storeId === 'company'))

  const signOut = async () => {
    setAccountOpen(false)
    await auth.logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''} ${isCompany ? 'scope-company' : isPinduoduo ? 'scope-pinduoduo' : isDouyin ? 'scope-douyin' : 'scope-jd'}`}>
      {mobileSidebarOpen ? <button className="sidebar-mobile-backdrop" type="button" aria-label="关闭导航" onClick={() => setMobileSidebarOpen(false)} /> : null}
      <aside className={`app-sidebar ${mobileSidebarOpen ? 'is-mobile-open' : ''}`}>
        <div className="sidebar-brand-row">
          <NavLink to={currentWorkspaceHome} className="site-brand" title={sidebarCollapsed ? '杯具熊经营台账' : undefined} onClick={() => setMobileSidebarOpen(false)}>
            <span className="site-brand-mark" aria-hidden="true">熊</span>
            <span>杯具熊经营台账</span>
          </NavLink>
          <button className="sidebar-mobile-close" type="button" aria-label="关闭导航" onClick={() => setMobileSidebarOpen(false)}><X aria-hidden="true" /></button>
        </div>

        <div className="workspace-switcher" ref={workspaceRef}>
          <span className="workspace-switcher-label">当前经营主体</span>
          <button
            className="workspace-trigger"
            type="button"
            aria-expanded={workspaceOpen}
            aria-haspopup="dialog"
            title={sidebarCollapsed ? `${currentWorkspace.platformName} · ${currentWorkspace.storeName}` : undefined}
            onClick={() => setWorkspaceOpen((open) => !open)}
          >
            <span className={`workspace-icon platform-${currentWorkspace.platformId}`}><WorkspaceIcon aria-hidden="true" /></span>
            <span className="workspace-trigger-copy">
              <small>{currentWorkspace.kind === 'company' ? '公司工作空间' : `${currentWorkspace.platformName} · 店铺`}</small>
              <strong>{currentWorkspace.storeName}</strong>
            </span>
            <ChevronDown aria-hidden="true" />
          </button>

          {workspaceOpen ? (
            <div className="workspace-panel" role="dialog" aria-label="切换经营主体">
              <div className="workspace-panel-heading"><div><strong>切换经营主体</strong><span>选择全公司或具体店铺</span></div><button type="button" aria-label="关闭" onClick={() => setWorkspaceOpen(false)}><X aria-hidden="true" /></button></div>
              <label className="workspace-search"><Search aria-hidden="true" /><input value={workspaceSearch} onChange={(event) => setWorkspaceSearch(event.target.value)} placeholder="搜索平台、店铺名称或店铺ID" autoFocus /></label>
              <div className="workspace-panel-scroll">
                {showCompanyWorkspace ? <section className="workspace-group">
                  <h3>全局</h3>
                  <button className={currentWorkspaceId === 'company' ? 'workspace-option is-current' : 'workspace-option'} type="button" onClick={switchToCompany}>
                    <span className="workspace-icon platform-company"><Building2 aria-hidden="true" /></span>
                    <span><strong>全公司经营中心</strong><small>公司产品、映射与跨店数据覆盖</small></span>
                    {currentWorkspaceId === 'company' ? <em>当前</em> : null}
                  </button>
                </section> : null}
                {groupedWorkspaces.map(([platformId, group]) => (
                  <section className="workspace-group" key={platformId}>
                    <h3>{group.name}（{group.stores.length}家）</h3>
                    {group.stores.map((option) => (
                      <button
                        className={option.id === currentWorkspaceId ? 'workspace-option is-current' : 'workspace-option'}
                        type="button"
                        key={option.id}
                        disabled={!option.route}
                        onClick={() => switchWorkspace(option)}
                      >
                        <span className={`workspace-icon platform-${option.platformId}`}><Store aria-hidden="true" /></span>
                        <span><strong>{option.storeName}</strong><small>{option.id}{option.route ? '' : ' · 页面待接入'}</small></span>
                        {option.id === currentWorkspaceId ? <em>当前</em> : option.route ? <ChevronRight aria-hidden="true" /> : null}
                      </button>
                    ))}
                  </section>
                ))}
                {!platformRegistry.loading && groupedWorkspaces.length === 0 ? <p className="workspace-empty">没有匹配的店铺。</p> : null}
              </div>
            </div>
          ) : null}
        </div>

        <SidebarNavigation sections={sections} collapsed={sidebarCollapsed} onNavigate={() => setMobileSidebarOpen(false)} />
        <button className="sidebar-collapse-button" type="button" onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}>
          {sidebarCollapsed ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
          <span>{sidebarCollapsed ? '展开导航' : '收起导航'}</span>
        </button>
        <div className="sidebar-account" ref={accountRef}>
          {accountOpen ? <div className="sidebar-account-menu" role="menu">
            <div className="sidebar-account-summary"><strong>{auth.user?.username}</strong><span>{auth.user?.phone}</span></div>
            {auth.user?.isAdmin ? <NavLink to="/admin/accounts" role="menuitem" onClick={() => setAccountOpen(false)}><ShieldCheck aria-hidden="true" /><span>账号与权限</span></NavLink> : null}
            <NavLink to="/change-password" role="menuitem" onClick={() => setAccountOpen(false)}><KeyRound aria-hidden="true" /><span>修改密码</span></NavLink>
            <button type="button" role="menuitem" onClick={() => void signOut()}><LogOut aria-hidden="true" /><span>退出登录</span></button>
          </div> : null}
          <button className="sidebar-account-trigger" type="button" aria-expanded={accountOpen} title={sidebarCollapsed ? auth.user?.username : undefined} onClick={() => setAccountOpen((open) => !open)}>
            <span className="sidebar-account-avatar"><UserRound aria-hidden="true" /></span>
            <span className="sidebar-account-copy"><strong>{auth.user?.username}</strong><small>{auth.user?.isAdmin ? '管理员' : '店铺运营'}</small></span>
            <ChevronDown aria-hidden="true" />
          </button>
        </div>
      </aside>

      <div className="app-body">
        <header className="site-header">
          <button className="sidebar-mobile-trigger" type="button" aria-label="打开导航" onClick={() => setMobileSidebarOpen(true)}><Menu aria-hidden="true" /></button>
          <div className="header-context">
            <span>{currentWorkspace.kind === 'company' ? '全公司' : `${currentWorkspace.platformName} · ${currentWorkspace.storeName}`}</span>
            <strong>{pageTitle}</strong>
          </div>

          {!isCompany && !isPinduoduo && !isDouyin && !isCalculator && !isAdvertisingOrders && !isFeeRatio && auth.canAccess(JD_STORE_ID, 'jd.sku') ? <div className="global-product-search">
            <label>
              <span className="sr-only">全局搜索商品</span><Search aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                placeholder="搜索商品 / SPU / SKU / 商家编码"
                onChange={(event) => { setSearchQuery(event.target.value); setSearchOpen(true) }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => window.setTimeout(() => setSearchOpen(false), 120)}
              />
            </label>
            {searchOpen && searchQuery.trim().length >= 2 ? (
              <div className="global-search-results" role="listbox" aria-label="商品搜索结果">
                {searchResults.map((product) => (
                  <button type="button" key={`${product.level}:${product.id}`} role="option" aria-selected="false" onMouseDown={(event) => event.preventDefault()} onClick={() => openProduct(product.level, product.id)}>
                    {product.image ? <img src={product.image} alt="" /> : <span className="global-search-image-fallback">无图</span>}
                    <span><strong>{product.nameNote || product.name}</strong><small>{product.level.toUpperCase()} {product.id}{product.level === 'sku' && product.merchantSku ? ` · 商家SKU ${product.merchantSku}` : ''}</small></span>
                  </button>
                ))}
                {!searchResults.length ? <p>没有匹配的商品。</p> : null}
              </div>
            ) : null}
          </div> : null}

          <div className="header-meta">
            {isCalculator
              ? null
              : isCompany
              ? <span className="company-context-badge">全公司 · 覆盖与能力</span>
              : isPinduoduo
                ? <span className="platform-context-badge">拼多多 · 独立口径</span>
                : isDouyin
                  ? <span className="platform-context-badge douyin">抖音 · 独立口径</span>
                : isFeeRatio
                  ? <span className="update-time">动态费比 · 日期见页面</span>
                : isAdvertisingOrders
                  ? <DataFreshnessBadge date={jdAdvertisingOrders?.dates.at(-1)} label="广告订单截至" mode="on-demand" compact />
                : isRealtime
                  ? <span className="update-time">历史快照，日期见页面</span>
                  : <DataFreshnessBadge date={data?.meta.maxDate} label="商品数据截至" compact />}
            {isPinduoduo || isDouyin || isCompany || isCalculator || isAdvertisingOrders || isFeeRatio ? null : isRealtime ? <span className="realtime-header-mode">暂停更新</span> : (
              <label className="date-control">
                <span>业务日期</span>
                <select value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} disabled={!data}>
                  {(data?.meta.dates || []).slice().reverse().map((date) => <option key={date} value={date}>{date}</option>)}
                </select>
                <CalendarDays aria-hidden="true" />
              </label>
            )}
          </div>
          {switchNotice ? <p className="workspace-switch-notice" role="status">{switchNotice}</p> : null}
        </header>
        <main className="app-main"><Outlet /></main>
      </div>
    </div>
  )
}
